
import { AccountType } from "../../Constants";
import { ContactType, LedgerType, PaymentType, TxnStatus, TxnType } from "../../types";
import { StandardField } from "../widgets/components/StandardFieldCompnents";

export interface TxnConfig {
  title: string;
  fields: StandardField[];
  contactType: ContactType,
  createRoute: string;
  listRoute?: string;
  viewRoute?: string;
  txnDirection: TxnDirection;
  paymentRelated?: boolean;
  paymentFlow?: PaymentFlow;
  returnType?: string;
  showConvert?: boolean;
  hideWithHoldingTax?: boolean;
  paymentType?: PaymentType;
  lockRate?:boolean;
  recurringEnabled?:boolean;
  useReordeQty?:boolean;
  txnAcctType?: AccountType;
  openTxnStatus?:TxnStatus[];
  defaultPackingSlip?: boolean;
}


export enum TxnDirection {
  Inward = 'Inward',
  Outward = 'Outward'
}

export enum PaymentFlow {
  Inward = 'Inward',
  Outward = 'Outward'
}

export const getTxnConfig = (txnType: TxnType): TxnConfig => {
  switch (txnType) {
    case TxnType.Invoice:
      return {
        title: "Invoice",
        fields: [StandardField.salesPerson],
        contactType: ContactType.Customer,
        createRoute: 'create-invoice',
        listRoute: '/transactions/view-invoices',
        txnDirection: TxnDirection.Inward,
        paymentRelated: true,
        paymentType: PaymentType.CustomerPayment,
        paymentFlow: PaymentFlow.Inward,
        lockRate:true,
        viewRoute: '/transactions/view-txn/' + txnType + '/',
        recurringEnabled:true,
        openTxnStatus:[TxnStatus.Saved,TxnStatus.Overdue,TxnStatus.PartiallyPaid],
        txnAcctType:AccountType.Income
      };
    case TxnType.PurchaseBills:
      return {
        title: "Purchase Bills",
        fields: [StandardField.poNum, StandardField.poDate],
        contactType: ContactType.Vendor,
        createRoute: 'create-purchase-bill',
        listRoute: '/transactions/view-purchase-bills',
        txnDirection: TxnDirection.Outward,
        paymentRelated: true,
        paymentType: PaymentType.VendorPayment,
        paymentFlow: PaymentFlow.Outward,
        recurringEnabled:true,
        viewRoute: '/transactions/view-txn/' + txnType + '/',
        useReordeQty:true,
        openTxnStatus:[TxnStatus.Saved,TxnStatus.Overdue,TxnStatus.PartiallyPaid],
        txnAcctType:AccountType.Expense
      };
    case TxnType.PurchaseOrder:
      return {
        title: "Purchase Orders",
        fields: [],
        contactType: ContactType.Vendor,
        createRoute: 'create-purchase-order',
        listRoute: '/transactions/view-purchase-orders',
        txnDirection: TxnDirection.Outward,
        showConvert: true,
        paymentFlow: PaymentFlow.Outward,
        viewRoute: '/transactions/view-txn/' + txnType + '/',
        useReordeQty:true
      };
    case TxnType.PurchaseReturn:
      return {
        title: "Purchase Return",
        fields: [],
        contactType: ContactType.Vendor,
        createRoute: 'create-purchase-return',
        listRoute: '/transactions/view-purchase-returns',
        txnDirection: TxnDirection.Outward,
        paymentRelated: false,
        paymentFlow: PaymentFlow.Inward,
        returnType: PaymentType.PurchaseReturnAllocation,
        hideWithHoldingTax: true,
        viewRoute: '/transactions/view-txn/' + txnType + '/',
        openTxnStatus:[TxnStatus.Saved,TxnStatus.PartiallyPaid],
        txnAcctType:AccountType.Expense
      };
    case TxnType.SalesOrder:
      return {
        title: "Sales Order",
        fields: [],
        contactType: ContactType.Customer,
        createRoute: 'create-sales-order',
        listRoute: '/transactions/view-sales-orders',
        txnDirection: TxnDirection.Inward,
        showConvert: true,
        lockRate:true,
        paymentFlow: PaymentFlow.Inward,
        viewRoute: '/transactions/view-txn/' + txnType + '/'
      };
    case TxnType.ProformaInvoice:
      return {
        title: "Proforma Invoice",
        fields: [StandardField.poNum, StandardField.poDate],
        contactType: ContactType.Customer,
        createRoute: 'create-proforma-invoice',
        listRoute: '/transactions/view-proforma-invoices',
        txnDirection: TxnDirection.Inward,
        showConvert: true,
        paymentFlow: PaymentFlow.Inward,
        viewRoute: '/transactions/view-txn/' + txnType + '/'
      };
    case TxnType.SalesReturn:
      return {
        title: "Sales Return",
        fields: [],
        contactType: ContactType.Customer,
        createRoute: 'create-sales-return',
        listRoute: '/transactions/view-sales-returns',
        txnDirection: TxnDirection.Inward,
        paymentRelated: false,
        paymentFlow: PaymentFlow.Outward,
        returnType: PaymentType.SalesReturnAllocation,
        hideWithHoldingTax: true,
        viewRoute: '/transactions/view-txn/' + txnType + '/',
        openTxnStatus: [TxnStatus.Saved, TxnStatus.PartiallyPaid],
        txnAcctType: AccountType.Income
      };
    case TxnType.Delivery:
      return {
        title: "Delivery",
        fields: [],
        contactType: ContactType.Customer,
        createRoute: 'create-delivery',
        listRoute: '/transactions/view-deliveries',
        txnDirection: TxnDirection.Inward,
        showConvert: true,
        paymentFlow: PaymentFlow.Inward,
        viewRoute: '/transactions/view-txn/' + txnType + '/',
        defaultPackingSlip: true
      };
    case TxnType.Estimate:
      return {
        title: "Estimate",
        fields: [],
        contactType: ContactType.Customer,
        createRoute: 'create-estimate',
        listRoute: '/transactions/view-estimates',
        txnDirection: TxnDirection.Inward,
        showConvert: true,
        paymentFlow: PaymentFlow.Inward,
        lockRate:true,
        viewRoute: '/transactions/view-txn/' + txnType + '/'
      };
    case TxnType.Expense:
      return {
        title: "Expense",
        fields: [],
        contactType: ContactType.Vendor,
        createRoute: 'create-expense',
        listRoute: '/transactions/view-expenses',
        txnDirection: TxnDirection.Outward,
        showConvert: true,
        // MBAI-86: only AP-routed expenses ever carry a balanceDue, so the Record Payment
        // actions (gated on balanceDue > 0) only appear for those.
        paymentRelated: true,
        paymentType: PaymentType.VendorPayment,
        paymentFlow: PaymentFlow.Outward,
        viewRoute: '/transactions/view-txn/' + txnType + '/'
      };
    case TxnType.StockAdjustment:
      return {
        title: "Stock Adjustment",
        fields: [],
        contactType: ContactType.Vendor,
        createRoute: 'create-stock-adjustment',
        listRoute: '/transactions/view-stock-adjustments',
        txnDirection: TxnDirection.Inward

      };
    default:
      throw new Error(`Unknown transaction type: ${txnType}`);
  }
};

export const getScanButtonLabel = (txnType: TxnType): string => {
  switch (txnType) {
    case TxnType.Expense:
      return 'Scan Expense';
    case TxnType.PurchaseBills:
      return 'Scan Purchase Bill';
    case TxnType.Bills:
      return 'Scan Bill';
    default:
      return 'Scan Document';
  }
};

export interface PaymentConfig {
  title: string;
  fields: StandardField[];
  contactType: ContactType,
  contactTxnType: TxnType | TxnType[],
  openingBalanceTxnType?: TxnType,
  contactTitle: string,
  createRoute?: string,
  viewRoute?: string,
  paymentAccountTitle?:string,
}

export const getPaymentConfig = (paymentType: PaymentType): PaymentConfig => {
  switch (paymentType) {
    case PaymentType.CustomerPayment:
      return {
        title: "Customer Payment",
        fields: [StandardField.salesPerson],
        contactType: ContactType.Customer,
        contactTxnType: TxnType.Invoice,
        openingBalanceTxnType: TxnType.OpeningReceivable,
        contactTitle: 'Customer',
        createRoute: '/transactions/create-customer-payment',
        viewRoute: '/transactions/view-payment/' + paymentType + '/',
        paymentAccountTitle:"Deposit To"
      };
    case PaymentType.VendorPayment:
      return {
        title: "Vendor Payment",
        fields: [],
        contactType: ContactType.Vendor,
        // MBAI-86: also fetch AP-routed Expenses so they show up as settleable, same as a Bill.
        // Non-AP expenses are always fully paid at creation (balanceDue 0), so including the
        // type here doesn't pull in anything convertToPaymentLines wouldn't already filter out.
        contactTxnType: [TxnType.PurchaseBills, TxnType.Expense],
        openingBalanceTxnType: TxnType.OpeningPayables,
        contactTitle: 'Vendor',
        createRoute: '/transactions/create-vendor-payment',
        viewRoute: '/transactions/view-payment/' + paymentType + '/',
        paymentAccountTitle:"Payment From"
      };
    case PaymentType.SalesReturnAllocation:
      return {
        title: "Apply Sales Return/Credit Note",
        fields: [],
        contactType: ContactType.Customer,
        openingBalanceTxnType: TxnType.OpeningReceivable,
        contactTxnType: TxnType.Invoice,
        contactTitle: 'customer',
        viewRoute: '/transactions/view-payment/' + paymentType + '/',
        paymentAccountTitle:"Payment From"
        // createRoute: '/transactions/create-vendor-payment'
      };
    case PaymentType.PurchaseReturnAllocation:
      return {
        title: "Apply Purchase Return/Debit Note",
        fields: [],
        contactType: ContactType.Vendor,
        contactTxnType: TxnType.PurchaseBills,
        openingBalanceTxnType: TxnType.OpeningPayables,
        contactTitle: 'Vendor',
        viewRoute: '/transactions/view-payment/' + paymentType + '/',
        paymentAccountTitle:"Deposit To"
        // createRoute: '/transactions/create-vendor-payment'
      };
    default:
      throw new Error(`Unknown payment type: ${paymentType}`);
  }
};

export interface LedgerConfig {
  title: string;
  fields?: StandardField[];
  openingBalanceTxnType?: TxnType,
  contactTitle?: string,
  createRoute?: string;
  editRoute?: string;
  viewRoute?: string;
}

export const getLedgerConfig = (ledgerType: LedgerType): LedgerConfig => {
  switch (ledgerType) {
    case LedgerType.Journal:
      return {
        title: 'Journal',
        createRoute: '/create-journal/',
        editRoute: '/transactions/edit-journal/',
        viewRoute: '/transactions/view-ledger/' + ledgerType + '/'
      }
      case LedgerType.BusinessTransaction:
        return {
          title: 'Business Transaction',
          createRoute: '/create-business-transaction/',
          editRoute: '/transactions/edit-business-transaction/',
          viewRoute: '/transactions/view-ledger/' + ledgerType + '/',
        }
    default:
      throw new Error(`Unknown transaction type: ${ledgerType}`);
  }
}
